// utils.js
// Shared utility functions for LukaIN platform

// --- Global State ---
window.preloaderCount = window.preloaderCount || 0;

/**
 * Universal JWT Decoder & Identity Parser
 */
export function get_parsed_jwt_identity() {
  const token = localStorage.getItem("access_token");
  if (!token) return null;

  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join(""),
    );
    const payload = JSON.parse(jsonPayload);

    if (payload.exp * 1000 < Date.now()) {
      return null;
    }

    if (payload.sub && typeof payload.sub === "object") {
      return payload.sub;
    }

    return payload.user_data || payload;
  } catch {
    return null;
  }
}

/**
 * Universal Date Formatter: DD/MM/YYYY
 */
export const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "Invalid Date";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

/**
 * Universal Toast Notification
 */
export function showToast(message, type = "info", options = {}) {
  const toastContainer = document.getElementById("toast-container");
  if (!toastContainer) return;

  const toast = document.createElement("div");
  toast.className = `toast toast-${type}`;

  const iconClass =
    {
      success: "fa-check-circle",
      error: "fa-exclamation-circle",
      warning: "fa-exclamation-triangle",
      info: "fa-info-circle",
    }[type] || "fa-info-circle";

  toast.innerHTML = `
        <div class="toast-content">
            <i class="fas ${iconClass}"></i>
            <span>${message}</span>
        </div>
        <button class="toast-close" aria-label="Close"><i class="fas fa-times"></i></button>
    `;

  toastContainer.appendChild(toast);
  setTimeout(() => {
    toast.classList.add("show");
  }, 10);

  const closeBtn = toast.querySelector(".toast-close");
  if (closeBtn) {
    closeBtn.onclick = () => {
      toast.classList.remove("show");
      setTimeout(() => {
        if (toast.parentNode) toast.remove();
      }, 400);
    };
  }

  setTimeout(() => {
    if (toast.parentNode) {
      toast.classList.remove("show");
      setTimeout(() => {
        if (toast.parentNode) toast.remove();
      }, 400);
    }
  }, options.duration || 4000);
}

/**
 * Global Preloader
 */
export function showGlobalPreloader(message = "Processing...") {
  window.preloaderCount++;
  const preloader = document.getElementById("global-preloader");
  if (preloader) {
    const preloaderMessage = document.getElementById(
      "global-preloader-message",
    );
    if (preloaderMessage) {
      if (window.preloaderCount === 1 || message !== "Processing...") {
        preloaderMessage.textContent = message;
      }
    }
    preloader.style.display = "flex";
  }
}

export function hideGlobalPreloader() {
  if (window.preloaderCount > 0) window.preloaderCount--;
  if (window.preloaderCount === 0) {
    const preloader = document.getElementById("global-preloader");
    if (preloader) preloader.style.display = "none";
  }
}

/**
 * Universal Custom Confirmation Modal
 * @param {string} title Modal title
 * @param {string} message Modal message
 * @param {string} type 'danger' (red), 'info' (blue), 'warning' (yellow)
 * @returns {Promise<boolean>}
 */
export function showCustomConfirm(title, message, type = "info") {
  return new Promise((resolve) => {
    // Check if modal already exists to prevent duplicates
    let modal = document.getElementById("custom-confirm-modal");
    if (!modal) {
      modal = document.createElement("div");
      modal.id = "custom-confirm-modal";
      modal.className = "custom-modal-overlay";
      modal.innerHTML = `
            <div class="custom-modal-content glass-card">
                <div class="custom-modal-header">
                    <h3 id="confirm-title">Confirm Action</h3>
                </div>
                <div class="custom-modal-body">
                    <p id="confirm-message">Are you sure you want to proceed?</p>
                </div>
                <div class="custom-modal-footer">
                    <button class="btn btn-secondary cancel-btn">Cancel</button>
                    <button class="btn btn-primary confirm-btn">Proceed</button>
                </div>
            </div>
        `;
      document.body.appendChild(modal);
    }

    const titleEl = document.getElementById("confirm-title");
    const messageEl = document.getElementById("confirm-message");
    const confirmBtn = modal.querySelector(".confirm-btn");
    const cancelBtn = modal.querySelector(".cancel-btn");

    titleEl.textContent = title;
    messageEl.textContent = message;

    // Apply specific type styles
    confirmBtn.className = `btn btn-primary confirm-btn ${type === "danger" ? "btn-danger" : type === "warning" ? "btn-warning" : ""}`;

    // Show modal with animation
    modal.classList.add("show");

    // Clear old event listeners
    const newConfirmBtn = confirmBtn.cloneNode(true);
    confirmBtn.parentNode.replaceChild(newConfirmBtn, confirmBtn);

    const newCancelBtn = cancelBtn.cloneNode(true);
    cancelBtn.parentNode.replaceChild(newCancelBtn, cancelBtn);

    newConfirmBtn.onclick = () => {
      modal.classList.remove("show");
      resolve(true);
    };

    newCancelBtn.onclick = () => {
      modal.classList.remove("show");
      resolve(false);
    };

    // Allow closing on overlay click
    modal.onclick = (e) => {
      if (e.target === modal) {
        modal.classList.remove("show");
        resolve(false);
      }
    };
  });
}

/**
 * Unified sidebar and navigation management for LukaIN
 */
export function initializeSidebar(userRole) {
  const sidebar = document.querySelector(".sidebar");
  if (!sidebar) return;

  if (!userRole) {
    userRole = localStorage.getItem("user_role");
  }

  if (!userRole) {
    return;
  }

  // 1. REBUILD SIDEBAR TO ENSURE TOP-DOWN ORDER
  sidebar.innerHTML = "";

  // 2. LOGO SECTION (ABSOLUTE TOP)
  const logoDiv = document.createElement("div");
  logoDiv.className = "logo";
  logoDiv.innerHTML = `
      <div class="logo-brand">
          <img src="LukaIN_System_Group_LOGO.png" alt="LukaIN Logo" class="logo-img" onerror="this.src='/favicon.png'">
          <span>LukaIN</span>
      </div>
      <button class="sidebar-close-btn hide-desktop" id="sidebar-close-btn" aria-label="Close sidebar">
          <i class="fas fa-times"></i>
      </button>
  `;
  sidebar.appendChild(logoDiv);

  // 3. NAVIGATION SECTION
  const nav = document.createElement("nav");
  nav.className = "sidebar-nav";
  sidebar.appendChild(nav);

  populateSidebarLinks(nav, userRole);
  injectUnifiedFooter();

  // 4. SETUP MOBILE TOGGLES
  // setupMobileToggles(); // Handled globally in common.js

  // Internal Close Button logic (Mobile only)
  const closeBtn = document.getElementById("sidebar-close-btn");
  if (closeBtn) {
    closeBtn.onclick = (e) => {
      e.preventDefault();
      const sidebarEl = document.querySelector(".sidebar");
      const toggle = document.querySelector(".sidebar-toggle");
      if (sidebarEl) sidebarEl.classList.remove("active");
      if (toggle) toggle.classList.remove("is-active");
      document.body.classList.remove("sidebar-open");

      const overlay = document.querySelector(".sidebar-overlay");
      if (overlay) overlay.style.display = "none";
    };
  }
}

function injectUnifiedFooter() {
  const mainContent = document.querySelector(".main-dashboard-content");
  if (!mainContent) return;

  // Remove ALL existing footers to prevent duplicates
  const existingFooters = document.querySelectorAll(
    "footer, .main-footer-dark, .hud-footer",
  );
  existingFooters.forEach((f) => f.remove());

  const footer = document.createElement("footer");
  footer.className = "main-footer-dark";
  footer.innerHTML = `
        <div class="footer-content">
            <div class="footer-logo">
                <div class="logo">
                    <img src="LukaIN_System_Group_LOGO.png" alt="LukaIN Logo" class="logo-img">
                    <span>LukaIN</span>
                </div>
                <p>Empowering organizations with Professional, automated management tools. Built for efficiency, designed for the future.</p>
            </div>
            <div class="footer-links">
                <h4>Product</h4>
                <a href="/landing#features">Key Features</a>
                <a href="/pricing">Pricing Plans</a>
                <a href="/help">Help Center</a>
                <a href="/support_ticket">Support Portal</a>
            </div>
            <div class="footer-links">
                <h4>Company</h4>
                <a href="/about">Our Story</a>
                <a href="/contact">Get in Touch</a>
                <a href="mailto:luckybasera123@gmail.com">Direct Email</a>
            </div>
            <div class="footer-links">
                <h4>Legal</h4>
                <a href="/terms">Terms of Service</a>
                <a href="/privacy_policy">Privacy Policy</a>
                <a href="/cancellations_refunds">Refund Policy</a>
                <a href="/deleteUser">Request Account Deletion</a>
            </div>
        </div>
        <div class="footer-bottom">
            <p>&copy; 2025-2026 LukaIN Technologies Pvt. Ltd. All rights reserved. Crafted with precision in India.</p>
        </div>
    `;
  mainContent.appendChild(footer);
}

function populateSidebarLinks(nav, userRole) {
  const currentPage = window.location.pathname.split("/").pop();
  const identity = get_parsed_jwt_identity();
  const workplaceType = identity
    ? identity.workplace_type || "School"
    : "School";

  // Customization mapping
  const labelsMap = {
    School: {
      members: "Students & Staff",
      erp: "School Tools",
      member_sub: "Students & Staff",
      schedule: "Timetable",
      attendance: "Attendance",
    },
    Coaching: {
      members: "Aspirants & Instructors",
      erp: "Coaching Tools",
      member_sub: "Aspirants & Instructors",
      schedule: "Batch Schedule",
      attendance: "Attendance",
    },
    Office: {
      members: "Team & Employees",
      erp: "Office Tools",
      member_sub: "Staff & Team",
      schedule: "Work Schedule",
      attendance: "Presence",
    },
    Hospital: {
      members: "Patients & Medical Staff",
      erp: "Hospital Tools",
      member_sub: "Patients & Doctors",
      schedule: "Rounds Schedule",
      attendance: "Rounds",
    },
    Factory: {
      members: "Workers & Supervisors",
      erp: "Factory Tools",
      member_sub: "Workers & Staff",
      schedule: "Shift Schedule",
      attendance: "Shifts",
    },
    Gym: {
      members: "Clients & Trainers",
      erp: "Gym Tools",
      member_sub: "Clients & Trainers",
      schedule: "Training Schedule",
      attendance: "Check-in",
    },
    Hotel: {
      members: "Guests & Hospitality Team",
      erp: "Hotel Tools",
      member_sub: "Guests & Staff",
      schedule: "Service Schedule",
      attendance: "Presence",
    },
    Other: {
      members: "Team Members",
      erp: "Main Tools",
      member_sub: "All Members",
      schedule: "Schedule",
      attendance: "Attendance",
    },
  };
  const labels = labelsMap[workplaceType] || labelsMap["Other"];

  const ROUTES_CONFIG = {
    admin: [
      {
        href: "admindashboard.html",
        icon: "fa-tachometer-alt",
        text: "Dashboard",
      },
      {
        href: "member_management.html",
        icon: "fa-users",
        text: labels.members,
      },
      { href: "erp_management.html", icon: "fa-book", text: labels.erp },
      {
        href: "timetable_management.html",
        icon: "fa-calendar-alt",
        text: labels.schedule,
      },
      { href: "messaging_center.html", icon: "fa-envelope", text: "Messaging" },
      { href: "attendance.html", icon: "fa-qrcode", text: "QR Attendance" },
      {
        href: "manual_attendance.html",
        icon: "fa-user-check",
        text: "Manual Attendance",
      },
      {
        href: "download_center.html",
        icon: "fa-download",
        text: "Download Center",
      },
      { href: "attendance_reports.html", icon: "fa-file-alt", text: "Reports" },
      { href: "settings.html", icon: "fa-cog", text: "Settings" },
    ],
    chairman: [
      {
        href: "superadmindashboard.html",
        icon: "fa-user-shield",
        text: "Dashboard",
      },
      {
        href: "organization_management.html",
        icon: "fa-building",
        text: "Organizations",
      },
      {
        href: "enterprise_requests.html",
        icon: "fa-briefcase",
        text: "Enterprise",
      },
      { href: "admin_approval.html", icon: "fa-user-check", text: "Approvals" },
      {
        href: "deletion_approval.html",
        icon: "fa-user-minus",
        text: "Deletion Requests",
      },
      { href: "system-metrics.html", icon: "fa-chart-bar", text: "Metrics" },
      { href: "manage_tickets.html", icon: "fa-ticket-alt", text: "Support" },
      { href: "superadmin-settings.html", icon: "fa-cog", text: "Settings" },
    ],
    teacher: [
      {
        href: "teacherdashboard.html",
        icon: "fa-tachometer-alt",
        text: "Dashboard",
      },
      {
        href: "member_management.html",
        icon: "fa-users",
        text: labels.member_sub,
      },
      { href: "attendance.html", icon: "fa-qrcode", text: "QR Attendance" },
      {
        href: "manual_attendance.html",
        icon: "fa-user-check",
        text: "Manual Attendance",
      },
    ],
    parent: [
      {
        href: "parentdashboard.html",
        icon: "fa-tachometer-alt",
        text: "Dashboard",
      },
      {
        href: "attendance_reports.html",
        icon: "fa-file-alt",
        text: labels.attendance || "Attendance",
      },
    ],
  };

  const roleLinks = ROUTES_CONFIG[userRole] || [];
  let linksHTML = "";
  roleLinks.forEach((link) => {
    const isActive = currentPage === link.href ? "active" : "";
    linksHTML += `<a href="${link.href}" class="${isActive}"><i class="icon fas ${link.icon}"></i>${link.text}</a>`;
  });
  nav.innerHTML = linksHTML;
}
